import * as React from "react";
import { Fragment } from "react";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router";
import { cn } from "../../lib/cn.js";

export interface Breadcrumb {
  label: string;
  /** In-app path; the last crumb (the current page) has none. */
  href?: string;
}

/** Breadcrumbs row (`17_UI_VISUAL_DESIGN_SYSTEM.md` §10), with client-side links. */
export function Breadcrumbs({ items, className }: { items: readonly Breadcrumb[]; className?: string }) {
  if (items.length === 0) return null;
  return (
    <nav aria-label="Breadcrumb" className={cn("flex min-w-0 flex-wrap items-center text-xs text-muted-foreground", className)}>
      <ol className="flex min-w-0 flex-wrap items-center">
        {items.map((crumb, index) => {
          const last = index === items.length - 1;
          return (
            <Fragment key={`${index}-${crumb.label}`}>
              {index > 0 && <ChevronRight className="mx-1 size-3 shrink-0" aria-hidden="true" />}
              <li className="min-w-0 truncate">
                {crumb.href && !last ? (
                  <Link to={crumb.href} className="transition-colors hover:text-foreground">
                    {crumb.label}
                  </Link>
                ) : (
                  <span {...(last ? { "aria-current": "page" as const } : {})}>{crumb.label}</span>
                )}
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}

const BreadcrumbTrailContext = React.createContext<readonly Breadcrumb[]>([]);

/**
 * The trail of parent pages for the current route (KG-13), supplied once by the app layout — e.g.
 * `Finance › Invoices` on an invoice's page. `PageHeader` shows it, ending with the page's own title,
 * so detail pages get breadcrumbs without each passing them. An empty trail shows none.
 */
export function BreadcrumbTrailProvider({ trail, children }: { trail: readonly Breadcrumb[]; children: React.ReactNode }) {
  return <BreadcrumbTrailContext.Provider value={trail}>{children}</BreadcrumbTrailContext.Provider>;
}

export function useBreadcrumbTrail(): readonly Breadcrumb[] {
  return React.useContext(BreadcrumbTrailContext);
}
